import React, { useState, useEffect, useCallback } from 'react';
import { purchasePass, claimPassReward, getActiveChallenges, claimChallengeReward } from '../utils/firebase';
import { levelFromTotalXp, s2TierFromXp } from '../constants/xp';
import { CREW_PASS_REWARDS, IMPOSTOR_PASS_REWARDS, PASS_COSTS } from '../constants/pass';
import { PASS_EXCLUSIVE_ITEMS } from '../constants/lootbox';
import ItemIcon from './lootbox/ItemIcon';
import { fmtTokens } from '../utils/helpers';
import { useToast } from './ToastContext';
import AchievementsPage from './AchievementsPage';
import { PgHeader, SubTabs } from './ui';

const TABS = [
  { id: 'crew',         label: '🔵 Crew Pass' },
  { id: 'impostor',     label: '🔴 Impostor Pass' },
  { id: 'achievements', label: '🏅 Achievements' },
];

function PassPage({ user, userData, onUserDataChange, isMobile, players, seasonConfig }) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('crew');
  const activePass = activeTab === 'achievements' ? 'crew' : activeTab;
  const [challenges, setChallenges] = useState(null);
  const [loadingChallenges, setLoadingChallenges] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const [claimingChallenge, setClaimingChallenge] = useState(null);
  const [tierPage, setTierPage] = useState(0);
  const [optimistic, setOptimistic] = useState(null);

  const loadChallenges = useCallback(async () => {
    try {
      setLoadingChallenges(true);
      const data = await getActiveChallenges();
      setChallenges(data);
    } catch (err) {
      console.error('Failed to load challenges:', err);
    } finally {
      setLoadingChallenges(false);
    }
  }, []);

  useEffect(() => { loadChallenges(); }, [loadChallenges]);

  const passOwnedForPage = activePass === 'crew' ? userData?.crewPassOwned : userData?.impostorPassOwned;
  const currentTierXpForPage = activePass === 'crew' ? (userData?.crewPassXp || 0) : (userData?.impostorPassXp || 0);
  const currentTierForPage = s2TierFromXp(currentTierXpForPage).tier;

  useEffect(() => {
    if (passOwnedForPage) {
      setTierPage(Math.floor(Math.max(currentTierForPage - 1, 0) / 10));
    } else {
      setTierPage(0);
    }
  }, [activePass, passOwnedForPage, currentTierForPage]);

  useEffect(() => { setOptimistic(null); }, [userData]);

  if (!userData) return null;

  const effective = optimistic ? { ...userData, ...optimistic } : userData;
  const level = effective.level || 1;
  const totalXp = effective.xp || 0;
  const levelInfo = levelFromTotalXp(totalXp);

  const passOwned = activePass === 'crew' ? effective.crewPassOwned : effective.impostorPassOwned;
  const passXp = activePass === 'crew' ? (effective.crewPassXp || 0) : (effective.impostorPassXp || 0);
  const passTierInfo = s2TierFromXp(passXp);
  const passTier = passTierInfo.tier;
  const passRewards = activePass === 'crew' ? CREW_PASS_REWARDS : IMPOSTOR_PASS_REWARDS;
  const claimed = effective.passRewardsClaimed || {};

  const passCost = PASS_COSTS[activePass];
  const tokenBalance = activePass === 'crew' ? (effective.crewmateTokens || 0) : (effective.imposterTokens || 0);
  const canAfford = tokenBalance >= passCost.amount;
  const isCrew = activePass === 'crew';
  const accent = isCrew ? 'var(--crew)' : 'var(--red)';
  const accentGlow = isCrew ? 'rgba(56, 189, 248, 0.35)' : 'rgba(232, 56, 74, 0.4)';

  const handlePurchase = async () => {
    if (purchasing) return;
    setPurchasing(true);
    const tokenField = isCrew ? 'crewmateTokens' : 'imposterTokens';
    const passField  = isCrew ? 'crewPassOwned' : 'impostorPassOwned';
    setOptimistic(prev => ({
      ...prev,
      [tokenField]: (userData[tokenField] || 0) - passCost.amount,
      [passField]: true,
    }));
    toast.success(`${isCrew ? 'CrewPass' : 'ImpostorPass'} purchased!`);
    purchasePass(activePass)
      .then(() => { if (onUserDataChange) onUserDataChange(); })
      .catch(err => { toast.error(err.message || 'Purchase failed'); setOptimistic(null); if (onUserDataChange) onUserDataChange(); })
      .finally(() => setPurchasing(false));
  };

  const handleClaimReward = async (tier) => {
    if (claiming) return;
    setClaiming(tier);
    const claimKey = `${activePass}_${tier}`;
    setOptimistic(prev => ({
      ...prev,
      passRewardsClaimed: { ...(userData.passRewardsClaimed || {}), ...(prev?.passRewardsClaimed || {}), [claimKey]: true },
    }));
    toast.success(`Tier ${tier} reward claimed!`);
    claimPassReward(activePass, tier)
      .then(() => { if (onUserDataChange) onUserDataChange(); })
      .catch(err => { toast.error(err.message || 'Claim failed'); setOptimistic(null); if (onUserDataChange) onUserDataChange(); })
      .finally(() => setClaiming(null));
  };

  const handleClaimChallenge = async (challengeId, challengeType) => {
    if (claimingChallenge) return;
    setClaimingChallenge(challengeId);
    if (challenges) {
      setChallenges(prev => ({
        ...prev,
        dailies: prev.dailies.map(c => c.id === challengeId ? { ...c, claimed: true } : c),
        weeklies: prev.weeklies.map(c => c.id === challengeId ? { ...c, claimed: true } : c),
      }));
    }
    claimChallengeReward(challengeId, challengeType)
      .then(result => { toast.success(`+${result.xpAwarded} XP!`); if (onUserDataChange) onUserDataChange(); loadChallenges(); })
      .catch(err => { toast.error(err.message || 'Claim failed'); loadChallenges(); })
      .finally(() => setClaimingChallenge(null));
  };

  const getRewardIcon = (reward) => {
    if (reward.type === 'tokens') return reward.tokenType === 'crewmate' ? '🔵' : '🔴';
    if (reward.type === 'lootbox') {
      const icons = { normal: '📦', shiny: '✨', elite: '💎', god: '👑', stattrak: '📊', vfxbox: '🎆' };
      return icons[reward.lootboxType] || '📦';
    }
    if (reward.type === 'item') {
      const item = PASS_EXCLUSIVE_ITEMS.find(i => i.id === reward.itemId);
      return item ? <ItemIcon item={item} size={20} /> : '🎁';
    }
    return '🎁';
  };

  const getRewardLabel = (reward) => {
    if (reward.type === 'tokens') return `${reward.amount} ${reward.tokenType === 'crewmate' ? 'Crew' : 'Imp'} Token${reward.amount > 1 ? 's' : ''}`;
    if (reward.type === 'lootbox') return `${reward.amount}x ${reward.lootboxType}`;
    if (reward.type === 'item') {
      const item = PASS_EXCLUSIVE_ITEMS.find(i => i.id === reward.itemId);
      return item ? item.name : 'Exclusive Item';
    }
    return 'Reward';
  };

  const formatCountdown = (timestamp) => {
    const diff = timestamp - Date.now();
    if (diff <= 0) return 'Now';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="v2-page">
      <PgHeader title="Battle Pass" sub={`Season 2 · ${passRewards.length} tiers`} />

      {/* Level Section */}
      <div className="v2-card" style={{ padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{
          minWidth: 64, height: 64, borderRadius: '50%',
          border: `2px solid ${accent}`,
          background: 'var(--elevated)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 14px ${accentGlow}`,
        }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 22, color: accent, lineHeight: 1 }}>{level}</div>
          <div style={{ fontSize: 8, fontFamily: "'Orbitron', sans-serif", letterSpacing: '0.1em', color: 'var(--text-2)', marginTop: 2 }}>LVL</div>
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)' }}>PLAYER LEVEL</span>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {levelInfo.xpToNext > 0 ? `${levelInfo.currentXp.toLocaleString()} / ${levelInfo.xpToNext.toLocaleString()} XP` : 'MAX'}
            </span>
          </div>
          <div style={{ background: 'var(--elevated)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
            <div style={{
              width: `${levelInfo.xpToNext > 0 ? (levelInfo.currentXp / levelInfo.xpToNext * 100) : 100}%`,
              height: '100%', background: accent, borderRadius: 6, transition: 'width 0.3s',
              boxShadow: `0 0 10px ${accentGlow}`,
            }} />
          </div>
        </div>
      </div>

      <SubTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'achievements' ? (
        <AchievementsPage user={user} userData={userData} players={players} isMobile={isMobile} seasonConfig={seasonConfig} embedded />
      ) : !passOwned ? (
        <div className="v2-card" style={{ padding: '28px 24px', textAlign: 'center', borderColor: accent + '55' }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>{isCrew ? '🔵' : '🔴'}</div>
          <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 22, fontWeight: 900, color: accent, marginBottom: 10 }}>
            {isCrew ? 'CrewPass' : 'ImpostorPass'}
          </h2>
          <p style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 16, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
            {passRewards.length} tiers of exclusive rewards including tokens, lootboxes, and pass-exclusive items!
          </p>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 20, color: accent, marginBottom: 6 }}>
            {isCrew ? '🔵' : '🔴'} {passCost.amount} {isCrew ? 'Crewmate' : 'Impostor'} Tokens
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 18 }}>
            Your balance: {fmtTokens(tokenBalance)} {isCrew ? '🔵' : '🔴'}
          </div>
          <button
            type="button"
            className="v2-btn v2-btn--primary"
            onClick={handlePurchase}
            disabled={!canAfford || purchasing}
            style={{ padding: '12px 28px', fontFamily: "'Orbitron', sans-serif", letterSpacing: '0.08em' }}
          >
            {purchasing ? 'Purchasing…' : canAfford ? 'Purchase Pass' : 'Not Enough Tokens'}
          </button>
        </div>
      ) : (
        <>
          {/* Current Tier Info */}
          <div className="v2-card" style={{ padding: '20px 24px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 11, color: accent, letterSpacing: '0.06em', marginBottom: 4 }}>
                  {isCrew ? '🔵 CREW PASS' : '🔴 IMPOSTOR PASS'} · OWNED
                </div>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 32, color: 'var(--text)' }}>
                  Tier {passTier}
                </div>
              </div>
              {passTier < passRewards.length && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>
                    {passRewards.length - passTier} tiers remaining
                  </div>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: 'var(--gold)' }}>
                    {passTierInfo.currentXp.toLocaleString()} / {passTierInfo.xpToNext.toLocaleString()} XP
                  </div>
                </div>
              )}
            </div>
            {passTier < passRewards.length && (
              <>
                <div style={{ background: 'var(--elevated)', borderRadius: 6, height: 8, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{
                    width: `${passTierInfo.xpToNext > 0 ? (passTierInfo.currentXp / passTierInfo.xpToNext * 100) : 0}%`,
                    height: '100%', background: accent, borderRadius: 6,
                    boxShadow: `0 0 12px ${accentGlow}`,
                  }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-2)' }}>Progress to Tier {passTier + 1} · earn XP by playing games</div>
              </>
            )}
          </div>

          {/* Tier Track — paginated */}
          {(() => {
            const PER = 10;
            const totalPages = Math.ceil(passRewards.length / PER);
            const pageStart = tierPage * PER;
            const pageTiers = passRewards.slice(pageStart, pageStart + PER);

            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)' }}>
                    TIERS {pageStart + 1}–{Math.min(pageStart + PER, passRewards.length)}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => setTierPage(p => Math.max(0, p - 1))}
                      disabled={tierPage === 0}
                      style={{
                        width: 30, height: 30, borderRadius: 7,
                        background: 'var(--elevated)', border: '1px solid var(--border)',
                        cursor: tierPage > 0 ? 'pointer' : 'not-allowed',
                        color: tierPage > 0 ? 'var(--text)' : 'var(--text-3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, opacity: tierPage === 0 ? 0.4 : 1,
                      }}
                    >◀</button>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setTierPage(i)}
                          style={{
                            width: i === tierPage ? 18 : 5, height: 5, borderRadius: 3,
                            background: i === tierPage ? accent : 'var(--elevated)',
                            cursor: 'pointer', transition: 'all 0.2s', border: 'none', padding: 0,
                          }}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setTierPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={tierPage === totalPages - 1}
                      style={{
                        width: 30, height: 30, borderRadius: 7,
                        background: 'var(--elevated)', border: '1px solid var(--border)',
                        cursor: tierPage < totalPages - 1 ? 'pointer' : 'not-allowed',
                        color: tierPage < totalPages - 1 ? 'var(--text)' : 'var(--text-3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, opacity: tierPage === totalPages - 1 ? 0.4 : 1,
                      }}
                    >▶</button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(5, 1fr)' : 'repeat(10, 1fr)', gap: isMobile ? 5 : 8, marginBottom: 28 }}>
                  {pageTiers.map(entry => {
                    const isUnlocked = entry.tier <= passTier;
                    const claimKey = `${activePass}_${entry.tier}`;
                    const isClaimed = claimed[claimKey];
                    const canClaim = isUnlocked && !isClaimed;
                    const isMilestone = entry.tier % 25 === 0 || entry.tier === passRewards.length;
                    const isCurrent = entry.tier === passTier;

                    return (
                      <div
                        key={entry.tier}
                        style={{
                          background: isClaimed ? 'rgba(74, 222, 128, 0.05)' : isCurrent ? accent + '16' : isUnlocked ? 'var(--elevated)' : 'var(--card)',
                          border: `1px solid ${isClaimed ? 'rgba(74, 222, 128, 0.22)' : isCurrent ? accent + '55' : isMilestone && isUnlocked ? 'var(--gold)' : 'var(--border)'}`,
                          borderRadius: 10,
                          padding: isMobile ? '10px 6px' : '12px 8px',
                          textAlign: 'center',
                          position: 'relative',
                          boxShadow: isCurrent ? `0 0 14px ${accentGlow}` : isMilestone && isUnlocked ? '0 0 10px rgba(255,215,0,0.25)' : 'none',
                        }}
                      >
                        {isCurrent && (
                          <div style={{
                            position: 'absolute', top: -7, left: '50%', transform: 'translateX(-50%)',
                            background: accent, color: '#000', fontSize: 7, fontWeight: 700,
                            fontFamily: "'Orbitron', sans-serif", padding: '2px 6px', borderRadius: 4,
                            letterSpacing: '0.04em', whiteSpace: 'nowrap',
                          }}>NOW</div>
                        )}
                        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, fontWeight: 700, color: isClaimed ? 'var(--green)' : isCurrent ? accent : 'var(--text-3)', letterSpacing: '0.06em', marginBottom: 4 }}>
                          T{entry.tier}
                        </div>
                        <div style={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 4, minHeight: 20 }}>
                          {entry.rewards.map((r, i) => (
                            <span key={i} style={{ fontSize: isMobile ? 14 : 16, lineHeight: 1, opacity: isUnlocked ? 1 : 0.35 }} title={getRewardLabel(r)}>
                              {getRewardIcon(r)}
                            </span>
                          ))}
                        </div>
                        {isClaimed && <div style={{ fontSize: 11, color: 'var(--green)' }}>✓</div>}
                        {canClaim && (
                          <button
                            type="button"
                            onClick={() => handleClaimReward(entry.tier)}
                            disabled={claiming === entry.tier}
                            style={{
                              marginTop: 4, width: '100%',
                              padding: '4px 6px', fontSize: 9,
                              background: accent, color: '#fff', border: 'none',
                              borderRadius: 5, cursor: 'pointer',
                              fontFamily: "'Orbitron', sans-serif", fontWeight: 700, letterSpacing: '0.05em',
                            }}
                          >
                            {claiming === entry.tier ? '…' : 'CLAIM'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* Challenges */}
          <div className="v2-card" style={{ padding: '18px 20px' }}>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', marginBottom: 14 }}>
              Challenges
            </div>
            {loadingChallenges ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>Loading challenges…</div>
            ) : challenges ? (
              <>
                <ChallengeGroup
                  title="Daily"
                  items={challenges.dailies}
                  resetLabel={formatCountdown(challenges.dailyResetAt)}
                  onClaim={id => handleClaimChallenge(id, 'daily')}
                  claimingId={claimingChallenge}
                  completeColor="var(--gold)"
                  barColor="var(--crew)"
                />
                <ChallengeGroup
                  title="Weekly"
                  items={challenges.weeklies}
                  resetLabel={formatCountdown(challenges.weeklyResetAt)}
                  onClaim={id => handleClaimChallenge(id, 'weekly')}
                  claimingId={claimingChallenge}
                  completeColor="var(--gold)"
                  barColor="var(--red)"
                />
              </>
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>Failed to load challenges</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ChallengeGroup({ title, items, resetLabel, onClaim, claimingId, completeColor, barColor }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text)', margin: 0 }}>
          {title.toUpperCase()}
        </h3>
        <span style={{ fontSize: 10, color: 'var(--text-2)' }}>Resets in {resetLabel}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(c => (
          <div key={c.id} style={{
            background: 'var(--elevated)',
            border: `1px solid ${c.completed ? completeColor + '44' : 'var(--border)'}`,
            borderRadius: 8,
            padding: '10px 14px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--text)' }}>{c.description}</span>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: 'var(--gold)' }}>+{c.xpReward} XP</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, background: 'var(--card)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min((c.progress / c.target) * 100, 100)}%`,
                  height: '100%',
                  background: c.completed ? completeColor : barColor,
                  borderRadius: 4,
                }} />
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-2)', minWidth: 42, textAlign: 'right' }}>{c.progress}/{c.target}</span>
              {c.completed && !c.claimed && (
                <button
                  type="button"
                  className="v2-btn v2-btn--primary"
                  onClick={() => onClaim(c.id)}
                  disabled={claimingId === c.id}
                  style={{ padding: '5px 12px', fontSize: 11 }}
                >
                  {claimingId === c.id ? '…' : 'Claim'}
                </button>
              )}
              {c.claimed && (
                <span style={{ fontSize: 10, color: 'var(--green)', fontFamily: "'Orbitron', sans-serif", fontWeight: 700, letterSpacing: '0.05em' }}>CLAIMED</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PassPage;
